/**
 * Wishlist page helpers: cart-lock submit guard, ATC loading UI, load shell,
 * merchandising CTA, and empty/guest guide copy for Swish cards.
 */
const WISHLIST_PAGE_PATH = '/apps/wishlist';
const COMING_SOON_TAGS = new Set(['shop-coming-soon', 'shop-coming-soon1']);
const SOLD_OUT_TAG = 'shop-sold-out';
const wishlistProductCache = new Map();

function setWishlistCardButtonLoading(form, isLoading) {
  const button = form?.querySelector('.wk-cta-button[type="submit"]');
  if (!button) {
    return;
  }

  if (isLoading) {
    button.setAttribute('aria-busy', 'true');
  } else {
    button.removeAttribute('aria-busy');
  }
}

function clearWishlistCardButtonLoading(form) {
  setWishlistCardButtonLoading(form, false);
}

function initWishlistPageLoadingState() {
  if (!window.location.pathname.includes(WISHLIST_PAGE_PATH)) {
    return;
  }

  const root = document.documentElement;
  root.classList.add('wishlist-page-loading');

  const markReady = () => {
    root.classList.remove('wishlist-page-loading');
    root.classList.add('wishlist-page-ready');
    observer.disconnect();
    clearTimeout(fallbackTimer);
  };

  const hasContent = () => {
    const page = document.querySelector('wishlist-page');
    if (!page) {
      return false;
    }

    if (page.querySelector('wishlist-product-card .wk-product-card .wk-image-link')) {
      return true;
    }

    if (page.querySelector('.wk-login-callout')) {
      return true;
    }

    if (
      page.querySelector('.wk-wishlist-empty-callout') ||
      page.querySelector('.wk-empty-callout') ||
      page.querySelector('.wk-empty')
    ) {
      return true;
    }

    const grid = page.querySelector('.wk-grid');
    if (grid && page.querySelector('.wk-header') && !page.querySelector('.wk-loading-spinner')) {
      return grid.childElementCount === 0 || Boolean(grid.querySelector('wishlist-product-card'));
    }

    return false;
  };

  if (hasContent()) {
    markReady();
    return;
  }

  const observer = new MutationObserver(() => {
    if (hasContent()) {
      markReady();
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  const fallbackTimer = window.setTimeout(markReady, 8000);
}

function normalizeProductTags(tags) {
  if (Array.isArray(tags)) {
    return tags.map((tag) => String(tag).trim().toLowerCase()).filter(Boolean);
  }
  if (typeof tags === 'string') {
    return tags
      .split(',')
      .map((tag) => tag.trim().toLowerCase())
      .filter(Boolean);
  }
  return [];
}

function resolveWishlistMerchReason(tags) {
  const tagSet = new Set(normalizeProductTags(tags));
  if ([...COMING_SOON_TAGS].some((tag) => tagSet.has(tag))) {
    return 'coming_soon';
  }
  if (tagSet.has(SOLD_OUT_TAG)) {
    return 'sold_out';
  }
  return null;
}

function getWishlistCardProductHandle(card) {
  const link = card.querySelector(
    '.wk-image-link[href*="/products/"], .wk-product-title a[href*="/products/"], a[href*="/products/"]'
  );
  if (!link?.getAttribute('href')) {
    return null;
  }

  try {
    const path = new URL(link.href, window.location.origin).pathname;
    const match = path.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function fetchWishlistProduct(handle) {
  if (wishlistProductCache.has(handle)) {
    return wishlistProductCache.get(handle);
  }

  const request = fetch(`/products/${handle}.js`)
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null);

  wishlistProductCache.set(handle, request);
  return request;
}

function setWishlistCtaLabel(button, label) {
  if (!button || !label) {
    return;
  }

  const labelEl = button.querySelector('.wk-cta-label');
  if (labelEl) {
    labelEl.textContent = label;
    return;
  }

  const contentPart = button.querySelector('[part="content"]');
  if (contentPart) {
    contentPart.textContent = label;
    return;
  }

  const buttonContent = button.querySelector('.button__content');
  if (buttonContent) {
    const nestedLabel = buttonContent.querySelector('span');
    if (nestedLabel) {
      nestedLabel.textContent = label;
      return;
    }
  }

  button.setAttribute('aria-label', label);
}

function applyWishlistMerchCta(card, reason) {
  const button = card.querySelector('.wk-cta-button');
  if (!button || !reason) {
    return;
  }

  const labels = window.smilodoxWishlistCta || {};
  const comingSoonClass = 'wk-cta-button--coming-soon';
  const soldOutClass = 'wk-cta-button--sold-out-tag';
  const expectedClass = reason === 'coming_soon' ? comingSoonClass : soldOutClass;

  if (button.dataset.merchReason === reason && button.classList.contains(expectedClass) && button.disabled) {
    return;
  }

  button.dataset.merchReason = reason;
  button.disabled = true;
  button.setAttribute('disabled', 'disabled');
  button.classList.remove(comingSoonClass, soldOutClass);

  if (reason === 'coming_soon') {
    button.classList.add(comingSoonClass);
    setWishlistCtaLabel(button, labels.comingSoon || button.textContent.trim());
  } else {
    button.classList.add(soldOutClass);
    setWishlistCtaLabel(button, labels.soldOut || button.textContent.trim());
  }
}

async function enhanceWishlistMerchCard(card) {
  if (!(card instanceof HTMLElement)) {
    return;
  }

  const existingReason = card.dataset.merchReason;
  if (existingReason) {
    applyWishlistMerchCta(card, existingReason);
    return;
  }

  if (card.dataset.merchPending === '1') {
    return;
  }

  const handle = getWishlistCardProductHandle(card);
  if (!handle) {
    return;
  }

  card.dataset.merchPending = '1';
  const product = await fetchWishlistProduct(handle);
  card.dataset.merchPending = '0';

  if (!product) {
    card.dataset.merchChecked = '1';
    return;
  }

  const reason = resolveWishlistMerchReason(product.tags);
  card.dataset.merchChecked = '1';
  if (!reason) {
    return;
  }

  card.dataset.merchReason = reason;
  applyWishlistMerchCta(card, reason);
}

const EMPTY_CALLOUT_SELECTOR = '.wk-wishlist-empty-callout, .wk-empty-callout';

function getWishlistGuide() {
  return window.smilodoxWishlistGuide || null;
}

function createTextElement(tagName, className, text) {
  const element = document.createElement(tagName);
  if (className) {
    element.className = className;
  }
  element.textContent = text;
  return element;
}

function emptyGuideIsCurrent(callout, guide) {
  const status = callout.querySelector('.wk-empty-status');
  return Boolean(status && status.textContent === guide.emptyStatus);
}

function loginGuideIsCurrent(callout, guide) {
  const heading = callout.querySelector('h2');
  return Boolean(heading && guide.saveTitle && heading.textContent.trim() === guide.saveTitle);
}

function enhanceWishlistEmptyCallout(page, guide) {
  if (page.querySelector('wishlist-product-card .wk-product-card .wk-image-link')) {
    return;
  }

  const callout = page.querySelector(EMPTY_CALLOUT_SELECTOR);
  if (!callout || emptyGuideIsCurrent(callout, guide)) {
    return;
  }

  callout.replaceChildren();
  callout.append(createTextElement('p', 'wk-empty-status', guide.emptyStatus));
  callout.append(createTextElement('p', 'wk-empty-howto', guide.emptyHowto));

  if (guide.shopUrl && guide.emptyShop) {
    const shopLink = document.createElement('a');
    shopLink.className = 'wk-empty-shop-link';
    shopLink.href = guide.shopUrl;
    shopLink.textContent = guide.emptyShop;
    callout.append(shopLink);
  }

  if (!guide.customerLoggedIn && guide.emptyGuestNote) {
    callout.append(createTextElement('p', 'wk-empty-guest-note', guide.emptyGuestNote));
  }
}

function enhanceWishlistLoginCallout(page, guide) {
  const callout = page.querySelector('.wk-login-callout');
  if (!callout || guide.customerLoggedIn) {
    return;
  }

  if (loginGuideIsCurrent(callout, guide)) {
    return;
  }

  const heading = callout.querySelector('h2');
  if (heading && guide.saveTitle) {
    heading.textContent = guide.saveTitle;
  }

  const body = callout.querySelector('.wk-login-content p');
  if (body && guide.saveBody) {
    const existingLogin = body.querySelector('a[href]');
    const loginHref = existingLogin?.getAttribute('href') || guide.loginUrl;
    body.replaceChildren();
    body.append(document.createTextNode(guide.saveBody));
    if (loginHref && guide.loginLink) {
      body.append(document.createTextNode(' '));
      const loginLink = document.createElement('a');
      loginLink.href = loginHref;
      loginLink.textContent = guide.loginLink;
      body.append(loginLink);
    }
  }
}

function initWishlistPageGuide() {
  if (!window.location.pathname.includes(WISHLIST_PAGE_PATH)) {
    return;
  }

  const guide = getWishlistGuide();
  if (!guide) {
    return;
  }

  let frame = 0;
  const apply = () => {
    if (frame) {
      return;
    }
    frame = window.requestAnimationFrame(() => {
      frame = 0;
      const page = document.querySelector('wishlist-page');
      if (!page) {
        return;
      }
      enhanceWishlistEmptyCallout(page, guide);
      enhanceWishlistLoginCallout(page, guide);
    });
  };

  apply();
  new MutationObserver(apply).observe(document.body, { childList: true, subtree: true });
}

function initWishlistMerchCtas() {
  if (!window.location.pathname.includes(WISHLIST_PAGE_PATH)) {
    return;
  }

  const scan = () => {
    document.querySelectorAll('wishlist-product-card').forEach((card) => {
      void enhanceWishlistMerchCard(card);
    });
  };

  scan();

  const observer = new MutationObserver(scan);
  observer.observe(document.body, { childList: true, subtree: true });
}

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target?.closest?.('wishlist-product-card .wk-form');
    if (!form) {
      return;
    }

    const merchButton = form.querySelector(
      '.wk-cta-button--coming-soon, .wk-cta-button--sold-out-tag, .wk-cta-button[data-merch-reason]'
    );
    if (merchButton) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (document.body.classList.contains('cart-lock--enabled')) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    setWishlistCardButtonLoading(form, true);

    const done = () => clearWishlistCardButtonLoading(form);

    document.addEventListener('cart:change', done, { once: true });
    document.addEventListener('cart:refresh', done, { once: true });
    window.setTimeout(done, 10000);
  },
  true
);

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initWishlistPageLoadingState();
      initWishlistPageGuide();
      initWishlistMerchCtas();
    },
    { once: true }
  );
} else {
  initWishlistPageLoadingState();
  initWishlistPageGuide();
  initWishlistMerchCtas();
}
